import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Plus, Edit2, Trash2, RefreshCw, TrendingUp, TrendingDown, Minus,
  X, ToggleLeft, ToggleRight, AlertCircle, CheckCircle, ChevronUp, ChevronDown
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';

const BRAND_BLUE = '#2B4B6A';

// Auto-fill defaults when a known ticker is entered
const TICKER_DEFAULTS = {
  // Agricultural
  'SB=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'KC=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'CC=F':  { unit: 'USD/MT',      mt_factor: 1.0      },
  'CT=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'OJ=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'ZS=F':  { unit: 'USc/bushel',  mt_factor: 0.36744  },
  'ZL=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'ZM=F':  { unit: 'USD/ton',     mt_factor: 1.10231  },
  'ZC=F':  { unit: 'USc/bushel',  mt_factor: 0.39368  },
  'ZW=F':  { unit: 'USc/bushel',  mt_factor: 0.36744  },
  'KE=F':  { unit: 'USc/bushel',  mt_factor: 0.36744  },
  'ZO=F':  { unit: 'USc/bushel',  mt_factor: 0.68893  },
  'ZR=F':  { unit: 'USD/cwt',     mt_factor: 22.0462  },
  // Livestock
  'LE=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'GF=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  'HE=F':  { unit: 'USc/lb',      mt_factor: 22.0462  },
  // Dairy
  'DC=F':  { unit: 'USD/cwt',     mt_factor: 22.0462  },
  // Energy
  'CL=F':  { unit: 'USD/barrel',  mt_factor: 7.33     },
  'BZ=F':  { unit: 'USD/barrel',  mt_factor: 7.33     },
  'NG=F':  { unit: 'USD/MMBtu',   mt_factor: null     },
  'HO=F':  { unit: 'USD/gal',     mt_factor: 315.0    },
  'RB=F':  { unit: 'USD/gal',     mt_factor: 358.0    },
  'EH=F':  { unit: 'USD/gal',     mt_factor: null     },
  // Precious Metals
  'GC=F':  { unit: 'USD/troy oz', mt_factor: 32150.75 },
  'SI=F':  { unit: 'USD/troy oz', mt_factor: 32150.75 },
  'PL=F':  { unit: 'USD/troy oz', mt_factor: 32150.75 },
  'PA=F':  { unit: 'USD/troy oz', mt_factor: 32150.75 },
  // Industrial Metals
  'HG=F':  { unit: 'USD/lb',      mt_factor: 2204.62  },
  'ALI=F': { unit: 'USc/lb',      mt_factor: 22.0462  },
  'TIOC.SI':{ unit: 'USD/MT',     mt_factor: 1.0      },
  // Vegetable Oils
  'LSOIL.SI': { unit: 'USD/MT',   mt_factor: 1.0      },
  'FCPO.KL': { unit: 'USD/MT',    mt_factor: 1.0      },
  'RSX=F':  { unit: 'CAD/MT',     mt_factor: null     },
  'SOYOIL.AS': { unit: 'USD/MT',  mt_factor: 1.0      },
  'CNYF.SI': { unit: 'USD/MT',    mt_factor: 1.0      },
  'COCOIL.AS': { unit: 'USD/MT',  mt_factor: 1.0      },
  // LME Metals
  'NI=F':   { unit: 'USD/MT',     mt_factor: 1.0      },
  'ZN=F':   { unit: 'USD/MT',     mt_factor: 1.0      },
  'SN=F':   { unit: 'USD/MT',     mt_factor: 1.0      },
  'PB=F':   { unit: 'USD/MT',     mt_factor: 1.0      },
  'CCSE.SZ': { unit: 'CNY/MT',    mt_factor: null     },
  'MOLYBDENUM.CMX': { unit: 'USD/lb', mt_factor: 2204.62 },
  'UX=F':   { unit: 'USD/lb',     mt_factor: 2204.62  },
  // Lumber & Forest
  'LBS=F':  { unit: 'USD/1000bd-ft', mt_factor: null },
  'PULP.SI': { unit: 'USD/MT',    mt_factor: 1.0      },
  // Fibers
  'RUBBER.CMX': { unit: 'USc/lb', mt_factor: 22.0462  },
  'WOOL.ASX': { unit: 'AUD/kg',   mt_factor: null     },
  // Crypto
  'BTC-USD': { unit: 'USD/BTC',   mt_factor: null     },
  'ETH-USD': { unit: 'USD/ETH',   mt_factor: null     },
  // Special
  'REE':    { unit: 'USD/kg',     mt_factor: null     },
  'LIT':    { unit: 'USD/MT',     mt_factor: 1.0      },
};

const MT_OPTIONS = [
  { value: '',         label: 'No conversion (show raw price only)' },
  { value: '22.0462',  label: 'USc/lb → USD/MT  ×22.0462  (Sugar, Coffee, Cotton, OJ, Cattle, Hogs, Aluminum)' },
  { value: '0.36744',  label: 'USc/bushel 60lb → USD/MT  ×0.36744  (Soybeans, Wheat, KC Wheat)' },
  { value: '0.39368',  label: 'USc/bushel 56lb → USD/MT  ×0.39368  (Corn)' },
  { value: '0.68893',  label: 'USc/bushel 32lb → USD/MT  ×0.68893  (Oats)' },
  { value: '1.0',      label: 'USD/MT already  ×1  (Cocoa, Iron Ore)' },
  { value: '1.10231',  label: 'USD/short ton → USD/MT  ×1.10231  (Soybean Meal)' },
  { value: '2204.62',  label: 'USD/lb → USD/MT  ×2204.62  (Copper)' },
  { value: '22.0462b', label: 'USD/cwt → USD/MT  ×22.0462  (Rice, Milk)' },
  { value: '7.33',     label: 'USD/barrel → USD/MT  ×7.33  (Crude Oil, Brent)' },
  { value: '315.0',    label: 'USD/gal → USD/MT  ×315  (Heating Oil)' },
  { value: '358.0',    label: 'USD/gal → USD/MT  ×358  (Gasoline RBOB)' },
  { value: '32150.75', label: 'USD/troy oz → USD/MT  ×32150.75  (Gold, Silver, Platinum, Palladium)' },
];

// Map select value → numeric factor (handles the duplicate 22.0462 for cwt)
const mtSelectToFactor = (val) => {
  if (!val) return null;
  if (val === '22.0462b') return 22.0462;
  return parseFloat(val) || null;
};

// Map numeric factor back to select value (best match)
const factorToMtSelect = (factor) => {
  if (factor == null) return '';
  // Check for cwt vs lb duplicate: if unit contains 'cwt', prefer 22.0462b, else 22.0462
  // We'll just default to the first match
  const match = MT_OPTIONS.find(o => parseFloat(o.value) === factor);
  return match ? match.value : String(factor);
};

const AdminCommodities = () => {
  const [commodities, setCommodities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshingId, setRefreshingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '', yahoo_ticker: '', unit: '', mt_factor_override: null, display_order: 0, enabled: true,
  });
  const [mtSelect, setMtSelect] = useState('');

  useEffect(() => { fetchCommodities(); }, []);

  const fetchCommodities = async () => {
    try {
      const res = await api.get('/admin/commodities/');
      setCommodities(res.data);
    } catch (err) {
      toast.error('Failed to load commodities');
    } finally {
      setLoading(false);
    }
  };

  const handleTickerChange = async (ticker) => {
    const upper = ticker.toUpperCase();
    const defaults = TICKER_DEFAULTS[upper];
    if (defaults) {
      const sel = defaults.mt_factor != null ? factorToMtSelect(defaults.mt_factor) : '';
      setForm(p => ({
        ...p,
        yahoo_ticker: upper,
        unit: p.unit || defaults.unit,
        mt_factor_override: defaults.mt_factor,
      }));
      setMtSelect(sel);
    } else {
      setForm(p => ({ ...p, yahoo_ticker: ticker }));
    }

    // Auto-populate name from Yahoo Finance shortName when name field is blank
    if (upper.length >= 3 && !form.name.trim()) {
      try {
        const res = await api.get(`/admin/commodities/ticker-lookup?ticker=${encodeURIComponent(upper)}`);
        if (res.data.short_name) {
          setForm(p => ({ ...p, name: p.name || res.data.short_name }));
        }
      } catch {
        // silently ignore — name stays blank for user to fill
      }
    }
  };

  const handleMtSelectChange = (val) => {
    setMtSelect(val);
    setForm(p => ({ ...p, mt_factor_override: mtSelectToFactor(val) }));
  };

  const handleSave = async () => {
    let saveForm = { ...form };

    // If name is blank but ticker is set, resolve name from Yahoo Finance before saving
    if (!saveForm.name.trim() && saveForm.yahoo_ticker) {
      try {
        const res = await api.get(`/admin/commodities/ticker-lookup?ticker=${encodeURIComponent(saveForm.yahoo_ticker)}`);
        if (res.data.short_name) {
          saveForm = { ...saveForm, name: res.data.short_name };
          setForm(p => ({ ...p, name: res.data.short_name }));
        }
      } catch {
        // proceed without name
      }
    }

    try {
      if (editing) {
        await api.put(`/admin/commodities/${editing}`, saveForm);
        toast.success('Commodity updated');
      } else {
        await api.post('/admin/commodities/', saveForm);
        toast.success('Commodity added');
      }
      resetForm();
      fetchCommodities();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its price history?`)) return;
    try {
      await api.delete(`/admin/commodities/${id}`);
      toast.success('Commodity deleted');
      fetchCommodities();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRefreshAll = async () => {
    setRefreshing(true);
    try {
      await api.post('/admin/commodities/refresh');
      toast.success('Price refresh started — check back in a moment');
      setTimeout(fetchCommodities, 5000);
    } catch (err) {
      toast.error('Failed to start refresh');
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefreshOne = async (id) => {
    setRefreshingId(id);
    try {
      const res = await api.post(`/admin/commodities/${id}/refresh`);
      if (res.data.success) {
        toast.success(`${res.data.commodity}: $${res.data.price} (${res.data.change_pct}%)`);
      } else {
        toast.error(`${res.data.commodity}: ${res.data.error}`);
      }
      fetchCommodities();
    } catch (err) {
      toast.error(`Refresh failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleEdit = (item) => {
    setEditing(item.id);
    const sel = item.mt_factor_override != null ? factorToMtSelect(item.mt_factor_override) : '';
    setMtSelect(sel);
    setForm({
      name: item.name || '',
      yahoo_ticker: item.yahoo_ticker || '',
      unit: item.unit || '',
      mt_factor_override: item.mt_factor_override ?? null,
      display_order: item.display_order || 0,
      enabled: item.enabled !== false,
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setEditing(null);
    setShowForm(false);
    setMtSelect('');
    setForm({ name: '', yahoo_ticker: '', unit: '', mt_factor_override: null, display_order: 0, enabled: true });
  };

  const handleMove = async (idx, direction) => {
    const swapIdx = idx + direction;
    if (swapIdx < 0 || swapIdx >= commodities.length) return;
    const a = commodities[idx];
    const b = commodities[swapIdx];
    const orderA = a.display_order ?? idx;
    const orderB = b.display_order ?? swapIdx;
    try {
      await Promise.all([
        api.put(`/admin/commodities/${a.id}`, { display_order: orderB }),
        api.put(`/admin/commodities/${b.id}`, { display_order: orderA }),
      ]);
      fetchCommodities();
    } catch {
      toast.error('Failed to reorder');
    }
  };

  const handleToggle = async (item) => {
    try {
      await api.put(`/admin/commodities/${item.id}`, { enabled: !item.enabled });
      fetchCommodities();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const trendIcon = (trend) => {
    if (trend === 'up') return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (trend === 'down') return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <Sidebar>
        <div className="p-8 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-6 md:p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commodity Price Tracker</h1>
            <p className="text-sm text-gray-500 mt-1">Manage commodities tracked via Yahoo Finance</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleRefreshAll}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition disabled:opacity-50"
              style={{ backgroundColor: BRAND_BLUE }}
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh All Prices
            </button>
            <button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition bg-green-600"
            >
              <Plus className="w-4 h-4" />
              Add Commodity
            </button>
          </div>
        </div>

        {/* Add/Edit Form */}
        {showForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{editing ? 'Edit' : 'Add'} Commodity</h2>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  value={form.name}
                  onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Sugar #11"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Yahoo Finance Ticker</label>
                <input
                  value={form.yahoo_ticker}
                  onChange={e => handleTickerChange(e.target.value)}
                  placeholder="e.g. SB=F, KC=F, ZS=F"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">Unit, MT conversion, and name auto-fill from Yahoo Finance when name is blank.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <input
                  value={form.unit}
                  onChange={e => setForm(p => ({ ...p, unit: e.target.value }))}
                  placeholder="Auto-filled from ticker (e.g. USc/lb)"
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">USD/MT Conversion</label>
                <select
                  value={mtSelect}
                  onChange={e => handleMtSelectChange(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                >
                  {MT_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                {form.mt_factor_override != null && (
                  <p className="text-xs text-blue-600 mt-1">
                    USD/MT = raw price × {form.mt_factor_override}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.display_order}
                  onChange={e => setForm(p => ({ ...p, display_order: parseInt(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e => setForm(p => ({ ...p, enabled: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label className="text-sm text-gray-700">Enabled</label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={resetForm} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
                style={{ backgroundColor: BRAND_BLUE }}
              >
                {editing ? 'Save Changes' : 'Add Commodity'}
              </button>
            </div>
          </div>
        )}

        {/* Commodities Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">#</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Commodity</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Price</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Change</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Ticker</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Last Updated</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {commodities.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400">
                    <TrendingUp className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No commodities configured</p>
                    <p className="text-sm mt-1 mb-4">Click "Add Commodity" to start tracking prices, or load the defaults:</p>
                    <button
                      onClick={async () => {
                        try {
                          const res = await api.post('/admin/commodities/seed');
                          toast.success(res.data.message);
                          fetchCommodities();
                        } catch (err) {
                          toast.error('Failed to seed commodities');
                        }
                      }}
                      className="px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition"
                      style={{ backgroundColor: BRAND_BLUE }}
                    >
                      Load 30 Default Commodities &amp; Minerals
                    </button>
                  </td>
                </tr>
              ) : (
                commodities.map((item, idx) => (
                  <tr key={item.id} className={`hover:bg-gray-50 ${!item.enabled ? 'opacity-50' : ''}`}>
                    <td className="px-4 py-3 text-sm text-gray-400">{idx + 1}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 text-sm">{item.name}</div>
                    </td>
                    <td className="px-4 py-3 min-w-[140px]">
                      {item.price_per_mt != null ? (
                        <div>
                          <div className="text-lg font-bold text-blue-700">
                            ${item.price_per_mt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            <span className="text-xs font-normal text-blue-500 ml-1">/MT</span>
                          </div>
                          {item.current_price != null && (
                            <div className="text-xs text-gray-400 mt-0.5">
                              {item.current_price.toFixed(4)} {item.unit || item.currency}
                            </div>
                          )}
                        </div>
                      ) : item.current_price != null ? (
                        <div>
                          <span className="text-base font-bold text-gray-900">{item.current_price.toFixed(4)}</span>
                          <span className="text-xs text-gray-400 ml-1">{item.unit || item.currency}</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400 italic">Not fetched</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {trendIcon(item.trend)}
                        <span className={`text-sm font-medium ${
                          item.trend === 'up' ? 'text-green-600' : item.trend === 'down' ? 'text-red-600' : 'text-gray-500'
                        }`}>
                          {item.change_pct != null ? `${item.change_pct > 0 ? '+' : ''}${item.change_pct.toFixed(2)}%` : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {item.yahoo_ticker ? (
                        <span className="text-xs font-mono bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{item.yahoo_ticker}</span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">Auto-detect</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500">
                      {item.last_updated ? new Date(item.last_updated).toLocaleString() : 'Never'}
                    </td>
                    <td className="px-4 py-3">
                      {item.last_error ? (
                        <div className="text-red-500" title={item.last_error}>
                          <div className="flex items-center gap-1">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span className="text-xs">Error</span>
                          </div>
                          <p className="text-xs text-red-400 mt-1 max-w-[200px] truncate">{item.last_error}</p>
                        </div>
                      ) : item.current_price != null ? (
                        <div className="flex items-center gap-1 text-green-500">
                          <CheckCircle className="w-4 h-4" />
                          <span className="text-xs">OK</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleMove(idx, -1)}
                          disabled={idx === 0}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-20"
                          title="Move up"
                        >
                          <ChevronUp className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleMove(idx, 1)}
                          disabled={idx === commodities.length - 1}
                          className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition disabled:opacity-20"
                          title="Move down"
                        >
                          <ChevronDown className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleRefreshOne(item.id)}
                          disabled={refreshingId === item.id}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Refresh price"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshingId === item.id ? 'animate-spin' : ''}`} />
                        </button>
                        <button
                          onClick={() => handleToggle(item)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title={item.enabled ? 'Disable' : 'Enable'}
                        >
                          {item.enabled ? <ToggleRight className="w-4 h-4 text-green-500" /> : <ToggleLeft className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => handleEdit(item)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id, item.name)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Info */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-2">How it works</h3>
            <ul className="text-xs text-blue-700 space-y-1">
              <li>1. Enter a ticker — unit and MT conversion auto-fill for known symbols</li>
              <li>2. If MT conversion is set, the price column shows USD/MT prominently</li>
              <li>3. The raw price (e.g. USc/lb) appears small below as reference</li>
              <li>4. Click "Refresh" to test, then prices update automatically each morning</li>
              <li>5. Price history is saved for charting and trend analysis</li>
            </ul>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <h3 className="text-sm font-semibold text-amber-800 mb-2">Yahoo Finance Tickers (30 pre-loaded)</h3>
            <div className="text-xs text-amber-700 grid grid-cols-3 gap-x-4 gap-y-0.5">
              <span className="font-semibold text-amber-900 col-span-3 mt-1">Agricultural</span>
              <span>Sugar #11: <strong>SB=F</strong></span>
              <span>Coffee: <strong>KC=F</strong></span>
              <span>Cocoa: <strong>CC=F</strong></span>
              <span>Cotton: <strong>CT=F</strong></span>
              <span>OJ: <strong>OJ=F</strong></span>
              <span>Soybeans: <strong>ZS=F</strong></span>
              <span>Soy Oil: <strong>ZL=F</strong></span>
              <span>Soy Meal: <strong>ZM=F</strong></span>
              <span>Corn: <strong>ZC=F</strong></span>
              <span>Wheat: <strong>ZW=F</strong></span>
              <span>KC Wheat: <strong>KE=F</strong></span>
              <span>Oats: <strong>ZO=F</strong></span>
              <span>Rice: <strong>ZR=F</strong></span>
              <span className="font-semibold text-amber-900 col-span-3 mt-1">Livestock &amp; Dairy</span>
              <span>Cattle: <strong>LE=F</strong></span>
              <span>Feeder: <strong>GF=F</strong></span>
              <span>Hogs: <strong>HE=F</strong></span>
              <span>Milk: <strong>DC=F</strong></span>
              <span className="font-semibold text-amber-900 col-span-3 mt-1">Energy</span>
              <span>WTI Crude: <strong>CL=F</strong></span>
              <span>Brent: <strong>BZ=F</strong></span>
              <span>Nat Gas: <strong>NG=F</strong></span>
              <span>Heating Oil: <strong>HO=F</strong></span>
              <span>Gasoline: <strong>RB=F</strong></span>
              <span>Ethanol: <strong>EH=F</strong></span>
              <span className="font-semibold text-amber-900 col-span-3 mt-1">Metals &amp; Minerals</span>
              <span>Gold: <strong>GC=F</strong></span>
              <span>Silver: <strong>SI=F</strong></span>
              <span>Platinum: <strong>PL=F</strong></span>
              <span>Palladium: <strong>PA=F</strong></span>
              <span>Copper: <strong>HG=F</strong></span>
              <span>Aluminum: <strong>ALI=F</strong></span>
              <span>Iron Ore: <strong>TIOC.SI</strong></span>
            </div>
          </div>
        </div>
      </div>
    </Sidebar>
  );
};

export default AdminCommodities;
